/**
 * Configuration management for playground
 * Handles API key and environment configuration with Rose storage
 */

import { roseStorage } from '@inboundx/shared/platform';

interface AppConfig {
    apiKey?: string;
    apiUrl: string;
    environment: 'development' | 'staging' | 'production';
    skipIframeForSites?: string[]; // List of domains/URLs to skip iframe for
    visitorIp?: string; // Fake visitor IP for testing enrichment
    visitorIpDomain?: string; // Domain/company name for the visitor IP
}

interface StoredConfig {
    apiKey?: string;
    apiUrl?: string;
    environment?: string;
    skipIframeForSites?: string[];
    customIframeSettings?: Record<string, boolean>; // Domain -> iframe enabled/disabled
    customSiteUrls?: Record<string, string>; // Domain/clientId -> custom URL to load
    visitorIp?: string; // Fake visitor IP for testing enrichment
    visitorIpDomain?: string; // Domain/company name for the visitor IP
}

function isAppEnvironment(value: string | undefined): value is AppConfig['environment'] {
    return value === 'development' || value === 'staging' || value === 'production';
}

// Default iframe settings per site
const DEFAULT_IFRAME_SETTINGS: Record<string, boolean> = {
    'abtasty.com': false, // Enable iframe for Abtasty (will use custom URL)
    'hyperline.co': false, // Block iframe: site sends frame-ancestors 'self' and X-Frame-Options SAMEORIGIN
    'skello.io': true, // Enable iframe for Skello (will use custom URL)
    'hexa.com': true, // Allow iframe for Hexa
    'pennylane.com': false, // Allow iframe for Pennylane
    'skaleet.com': false, // Allow iframe for Skaleet
    // Add more default settings as needed
};

// Default custom URLs for sites that need them
const DEFAULT_CUSTOM_URLS: Record<string, string> = {
    skello: 'https://storage.googleapis.com/ix_web_dumps/skello.html',
    'skello.io': 'https://storage.googleapis.com/ix_web_dumps/skello.html',
    'www.skello.io': 'https://storage.googleapis.com/ix_web_dumps/skello.html',
    mayday: 'https://storage.googleapis.com/ix_web_dumps/mayday.html',
    'mayday.fr': 'https://storage.googleapis.com/ix_web_dumps/mayday.html',
    //   pennylane: 'https://storage.googleapis.com/ix_web_dumps/pennylane.html',
    //   'pennylane.com': 'https://storage.googleapis.com/ix_web_dumps/pennylane.html',
    //   skaleet: 'https://storage.googleapis.com/ix_web_dumps/skaleet.html',
    //   'skaleet.com': 'https://storage.googleapis.com/ix_web_dumps/skaleet.html',

    // Add more custom URLs as needed
};

// Default visitor IP disabled - no enrichment injection by default
const DEFAULT_VISITOR_IP = '';
const DEFAULT_VISITOR_IP_DOMAIN = '';

/**
 * Get configuration from Rose storage
 */
function getStoredConfig(): StoredConfig {
    try {
        const config = roseStorage.getPreprodConfig();
        const iframe = roseStorage.getIframeSettings();
        const customUrls = roseStorage.getCustomUrls();
        const visitorIp = roseStorage.getVisitorIp();
        const visitorIpDomain = roseStorage.getVisitorIpDomain();

        return {
            apiKey: config.apiKey,
            apiUrl: config.apiUrl,
            environment: config.environment,
            customIframeSettings: iframe,
            customSiteUrls: customUrls,
            skipIframeForSites: [], // Legacy, migrated on first access
            visitorIp: visitorIp || DEFAULT_VISITOR_IP,
            visitorIpDomain: visitorIpDomain || DEFAULT_VISITOR_IP_DOMAIN,
        };
    } catch (error) {
        console.warn('Failed to load config from Rose storage:', error);
        return { visitorIp: DEFAULT_VISITOR_IP, visitorIpDomain: DEFAULT_VISITOR_IP_DOMAIN };
    }
}

/**
 * Save configuration to Rose storage
 */
export function saveConfig(config: Partial<StoredConfig>): void {
    try {
        // Update preprod config
        if (
            config.apiKey !== undefined ||
            config.apiUrl !== undefined ||
            config.environment !== undefined
        ) {
            roseStorage.updatePreprodConfig({
                apiKey: config.apiKey,
                apiUrl:
                    config.apiUrl !== undefined
                        ? config.apiUrl
                        : roseStorage.getPreprodConfig().apiUrl,
                environment:
                    (config.environment as 'development' | 'staging' | 'production') ||
                    'development',
            });
        }

        // Update iframe settings
        if (config.customIframeSettings) {
            Object.entries(config.customIframeSettings).forEach(([domain, allowed]) => {
                roseStorage.setIframeSetting(domain, allowed);
            });
        }

        // Update custom URLs
        if (config.customSiteUrls) {
            Object.entries(config.customSiteUrls).forEach(([key, url]) => {
                roseStorage.setCustomUrl(key, url);
            });
        }

        // Update visitor IP
        if (config.visitorIp !== undefined) {
            roseStorage.setVisitorIp(config.visitorIp);
        }

        // Update visitor IP domain
        if (config.visitorIpDomain !== undefined) {
            roseStorage.setVisitorIpDomain(config.visitorIpDomain);
        }
    } catch (error) {
        console.warn('Failed to save config to Rose storage:', error);
    }
}

/**
 * Get merged configuration (Rose storage only for playground)
 * Priority: Rose storage > defaults
 */
export function getConfig(): AppConfig {
    const storedConfig = getStoredConfig();

    return {
        apiKey: storedConfig.apiKey,
        apiUrl: storedConfig.apiUrl || '',
        environment: isAppEnvironment(storedConfig.environment)
            ? storedConfig.environment
            : 'development',
        skipIframeForSites: storedConfig.skipIframeForSites || [],
        visitorIp: storedConfig.visitorIp || DEFAULT_VISITOR_IP,
        visitorIpDomain: storedConfig.visitorIpDomain || DEFAULT_VISITOR_IP_DOMAIN,
    };
}

/**
 * Check if API key is configured
 */
export function hasApiKey(): boolean {
    const config = getConfig();
    return !!config.apiKey;
}

/**
 * Get API key with fallback message
 */
export function getApiKey(): string | null {
    const config = getConfig();
    return config.apiKey || null;
}

/**
 * Clear all stored configuration
 */
export function clearConfig(): void {
    try {
        roseStorage.clearPreprodData();
    } catch (error) {
        console.warn('Failed to clear config from Rose storage:', error);
    }
}

/**
 * Get the domain from a URL
 */
function getDomainFromUrl(url: string): string | null {
    try {
        const urlObj = new URL(url);
        return urlObj.hostname.replace('www.', '');
    } catch {
        return null;
    }
}

/**
 * Check if iframe should be enabled for a URL
 * Priority: Custom settings > Default settings > Legacy skipIframeForSites > true (allow iframe)
 */
export function shouldAllowIframe(url: string): boolean {
    const domain = getDomainFromUrl(url);
    if (!domain) return true;

    const storedConfig = getStoredConfig();

    // Check custom settings first
    if (storedConfig.customIframeSettings && domain in storedConfig.customIframeSettings) {
        return storedConfig.customIframeSettings[domain];
    }

    // Check default settings
    if (domain in DEFAULT_IFRAME_SETTINGS) {
        return DEFAULT_IFRAME_SETTINGS[domain];
    }

    // Check legacy skipIframeForSites setting
    const skipSites = storedConfig.skipIframeForSites || [];
    const shouldSkip = skipSites.some((site) => {
        return domain.includes(site) || url.includes(site);
    });

    return !shouldSkip; // Invert because skipIframeForSites means "don't allow iframe"
}

/**
 * Check if a URL should skip iframe loading (inverse of shouldAllowIframe)
 */
export function shouldSkipIframe(url: string): boolean {
    return !shouldAllowIframe(url);
}

/**
 * Set iframe preference for a specific domain
 */
export function setIframePreference(url: string, allowIframe: boolean): void {
    const domain = getDomainFromUrl(url);
    if (!domain) return;

    const storedConfig = getStoredConfig();
    const customSettings = storedConfig.customIframeSettings || {};

    customSettings[domain] = allowIframe;
    saveConfig({ customIframeSettings: customSettings });
}

/**
 * Get iframe setting source for a URL (default, custom, or legacy)
 */
export function getIframeSettingSource(url: string): 'default' | 'custom' | 'legacy' | 'none' {
    const domain = getDomainFromUrl(url);
    if (!domain) return 'none';

    const storedConfig = getStoredConfig();

    // Check if there's a custom setting
    if (storedConfig.customIframeSettings && domain in storedConfig.customIframeSettings) {
        return 'custom';
    }

    // Check if there's a default setting
    if (domain in DEFAULT_IFRAME_SETTINGS) {
        return 'default';
    }

    // Check legacy skipIframeForSites
    const skipSites = storedConfig.skipIframeForSites || [];
    const hasLegacySetting = skipSites.some((site) => {
        return domain.includes(site) || url.includes(site);
    });

    return hasLegacySetting ? 'legacy' : 'none';
}

/**
 * Clear custom setting for a domain (revert to default)
 */
export function clearCustomIframeSetting(url: string): void {
    const domain = getDomainFromUrl(url);
    if (!domain) return;

    const storedConfig = getStoredConfig();
    const customSettings = storedConfig.customIframeSettings || {};

    delete customSettings[domain];
    saveConfig({ customIframeSettings: customSettings });
}

/**
 * Get custom URL for a client/site
 * Priority: User-configured custom URLs > Default custom URLs > null
 */
export function getCustomSiteUrl(clientId: string, domain?: string): string | null {
    const storedConfig = getStoredConfig();

    // Check user-configured custom URLs first
    if (storedConfig.customSiteUrls) {
        // Try client ID first
        if (clientId && storedConfig.customSiteUrls[clientId]) {
            return storedConfig.customSiteUrls[clientId];
        }
        // Try domain if provided
        if (domain && storedConfig.customSiteUrls[domain]) {
            return storedConfig.customSiteUrls[domain];
        }
    }

    // Check default custom URLs
    if (clientId && DEFAULT_CUSTOM_URLS[clientId]) {
        return DEFAULT_CUSTOM_URLS[clientId];
    }
    if (domain && DEFAULT_CUSTOM_URLS[domain]) {
        return DEFAULT_CUSTOM_URLS[domain];
    }

    return null;
}

/**
 * Validate if a string is a valid URL
 */
function isValidUrl(urlString: string): boolean {
    try {
        const url = new URL(urlString);
        // Only allow http and https protocols
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
}

/**
 * Set a custom URL for a specific client/site
 */
export function setCustomSiteUrl(clientId: string, url: string | null): void {
    const storedConfig = getStoredConfig();
    const customUrls = storedConfig.customSiteUrls || {};

    if (url === null || url === '') {
        // Remove the custom URL
        delete customUrls[clientId];
    } else {
        // Validate URL before saving
        if (!isValidUrl(url)) {
            throw new Error(`Invalid URL provided: ${url}. URL must be a valid http or https URL.`);
        }
        // Set the custom URL
        customUrls[clientId] = url;
    }

    saveConfig({ customSiteUrls: customUrls });
}

/**
 * Get all custom URLs (both default and user-configured)
 */
export function getAllCustomUrls(): Record<string, { url: string; source: 'default' | 'custom' }> {
    const storedConfig = getStoredConfig();
    const result: Record<string, { url: string; source: 'default' | 'custom' }> = {};

    // Add default URLs
    Object.entries(DEFAULT_CUSTOM_URLS).forEach(([key, url]) => {
        result[key] = { url, source: 'default' };
    });

    // Override with custom URLs
    if (storedConfig.customSiteUrls) {
        Object.entries(storedConfig.customSiteUrls).forEach(([key, url]) => {
            result[key] = { url, source: 'custom' };
        });
    }

    return result;
}

/**
 * Clear custom URL for a client (revert to default if exists)
 */
export function clearCustomSiteUrl(clientId: string): void {
    setCustomSiteUrl(clientId, null);
}

/**
 * Get config for display/debugging
 */
export type ConfigSummary = ReturnType<typeof getConfigSummary>;

export function getConfigSummary(): {
    hasApiKey: boolean;
    apiUrl: string;
    environment: string;
    skipIframeForSites: string[];
    customSiteUrls: Record<string, string>;
    visitorIp: string;
    visitorIpDomain: string;
    source: {
        apiKey: 'localStorage' | 'none';
        apiUrl: 'localStorage' | 'default';
        visitorIp: 'localStorage' | 'default';
        visitorIpDomain: 'localStorage' | 'default';
    };
} {
    const storedConfig = getStoredConfig();
    const finalConfig = getConfig();

    return {
        hasApiKey: !!finalConfig.apiKey,
        apiUrl: finalConfig.apiUrl,
        environment: finalConfig.environment,
        skipIframeForSites: finalConfig.skipIframeForSites || [],
        customSiteUrls: storedConfig.customSiteUrls || {},
        visitorIp: finalConfig.visitorIp || DEFAULT_VISITOR_IP,
        visitorIpDomain: finalConfig.visitorIpDomain || DEFAULT_VISITOR_IP_DOMAIN,
        source: {
            apiKey: storedConfig.apiKey ? 'localStorage' : 'none',
            apiUrl: storedConfig.apiUrl ? 'localStorage' : 'default',
            visitorIp:
                storedConfig.visitorIp && storedConfig.visitorIp !== DEFAULT_VISITOR_IP
                    ? 'localStorage'
                    : 'default',
            visitorIpDomain:
                storedConfig.visitorIpDomain &&
                storedConfig.visitorIpDomain !== DEFAULT_VISITOR_IP_DOMAIN
                    ? 'localStorage'
                    : 'default',
        },
    };
}
